import { useTranslation } from "@sagot/i18n";
import { Chip, Icon, type ChipTone } from "@sagot/ui";
import { useFormatters } from "../format";
import { isAccountStatus, isContextSource, statusTone, tierLabel, type CustomerTier } from "./customer-context-model";

const STATUS_CHIP: Record<ReturnType<typeof statusTone>, { tone: ChipTone; icon: "check-circle" | "alert-triangle" | "x-circle" | "circle" }> = {
  success: { tone: "success", icon: "check-circle" },
  warning: { tone: "warning", icon: "alert-triangle" },
  danger: { tone: "danger", icon: "x-circle" },
  neutral: { tone: "muted", icon: "circle" },
};

/** The customer's account status: icon + words + tone, never colour alone. */
export function AccountStatusChip({ status }: { status: string | null }) {
  const { t } = useTranslation();
  if (!status) return <span className="text-fg-muted">{t("customerContext.card.notSet")}</span>;
  const spec = STATUS_CHIP[statusTone(status)];
  return (
    <Chip tone={spec.tone} icon={spec.icon} data-testid="customer-status">
      {isAccountStatus(status) ? t(`customerContext.card.statuses.${status}`) : status}
    </Chip>
  );
}

export function TierChip({ tier }: { tier: CustomerTier | null }) {
  const { t, i18n } = useTranslation();
  if (!tier) return <span className="text-fg-muted">{t("customerContext.card.notSet")}</span>;
  return (
    <Chip tone="accent" icon="badge-check" data-testid="customer-tier">
      {tierLabel(tier, i18n.language)}
    </Chip>
  );
}

/** Where a value came from, as words: "Context API". */
export function useSourceLabel(): (source: string | null) => string {
  const { t } = useTranslation();
  return (source) => (isContextSource(source) ? t(`customerContext.fields.source.${source}`) : (source ?? ""));
}

/** "Context API · 08:12": the source chip next to a value (CRM-002, PDI-009). */
export function SourceChip({ source, asOf }: { source: string | null; asOf: string | null }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const label = useSourceLabel();
  if (!source) return null;
  const text = asOf ? t("customerContext.fields.sourceAt", { source: label(source), time: fmt.time(asOf) }) : label(source);
  return (
    <Chip tone="outline" icon="plug" data-testid="source-chip">
      {text}
    </Chip>
  );
}

/** A field's value as text: yes/no for flags, a list joined, numbers in the workspace's format. */
export function useFieldText(): (value: unknown) => string {
  const { t } = useTranslation();
  const fmt = useFormatters();
  return (value) => {
    if (value === null || value === undefined || value === "") return t("customerContext.fields.noValue");
    if (typeof value === "boolean") return value ? t("customerContext.fields.yes") : t("customerContext.fields.no");
    if (typeof value === "number") return fmt.number(value);
    if (Array.isArray(value)) return value.map((entry) => String(entry)).join(", ");
    return typeof value === "string" ? value : JSON.stringify(value);
  };
}

/** Marks a personal-data field so staff know it needs care. */
export function PersonalMark() {
  const { t } = useTranslation();
  return (
    <span title={t("customerContext.card.personal")} className="inline-flex align-middle text-fg-subtle">
      <Icon name="lock" className="size-3" label={t("customerContext.card.personal")} />
    </span>
  );
}
